import type { ReactNode } from 'react';
import { useSlots } from '@gonfalon/react/useSlots';
import clsx from 'clsx';
import invariant from 'tiny-invariant';

import { SettingsCardTabs } from './SettingsCardTabs';

import styles from './SettingsCard.module.css';

type LayoutMode = null | 'collection' | 'tabbed' | 'section';

export function SettingsCard({
  destructive,
  children,
  withBorder,
}: {
  destructive?: boolean;
  children: ReactNode;
  withBorder?: boolean;
}) {
  const [{ header, banner, collection, section, footer, tabs, toolbar }] = useSlots(children, {
    header: SettingsCardHeader,
    banner: SettingsCardBanner,
    toolbar: SettingsCardToolbar,
    collection: SettingsCardCollection,
    tabs: SettingsCardTabs,
    section: SettingsCardSection,
    footer: SettingsCardFooter,
  });

  let layoutMode: LayoutMode = null;
  if (collection) {
    layoutMode = 'collection';
  } else if (tabs) {
    layoutMode = 'tabbed';
  } else if (section) {
    layoutMode = 'section';
  }

  if (layoutMode === 'collection') {
    invariant(collection, 'SettingsCard must include a SettingsCardCollection');
  } else if (layoutMode === 'tabbed') {
    invariant(tabs, 'SettingsCard must include a SettingsCardTabs');
  } else if (layoutMode === 'section') {
    invariant(section, 'SettingsCard must include a SettingsCardSection');
  }

  return (
    <div className={styles.card}>
      <div className={clsx(styles.body, { [styles.destructive]: destructive, [styles.withBorder]: withBorder })}>
        {header}
        {banner}
        {toolbar}
        {collection || tabs || section}
      </div>
      {footer}
    </div>
  );
}

export function SettingsCardHeader({ children }: { children: ReactNode }) {
  const [{ navigation, title, description, cta }] = useSlots(children, {
    navigation: SettingsCardNavigation,
    title: SettingsCardTitle,
    description: SettingsCardDescription,
    cta: SettingsCardCta,
  });
  return (
    <div className={styles.header}>
      {navigation}
      <div className={styles.instructions}>
        {title}
        {description}
      </div>
      <div>{cta}</div>
    </div>
  );
}

export function SettingsCardBanner({ children }: { children: ReactNode }) {
  return <div className={styles.banner}>{children}</div>;
}

export function SettingsCardToolbar({ children }: { children: ReactNode }) {
  const [{ search, filter }] = useSlots(children, {
    search: SettingsCardSearch,
    filter: SettingsFilter,
  });

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarLeft}>
        {search}
        {filter}
      </div>
    </div>
  );
}

export function SettingsCardNavigation({ children }: { children: ReactNode }) {
  return <div className={styles.navigation}>{children}</div>;
}

export function SettingsCardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h2 className={clsx(styles.title, className)}>{children}</h2>;
}

export function SettingsCardDescription({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={clsx(styles.description, className)}>{children}</p>;
}

export function SettingsCardSearch({ children }: { children: ReactNode }) {
  return <div className={styles.search}>{children}</div>;
}

export function SettingsFilter({ children }: { children: ReactNode }) {
  return <div className={styles.filter}>{children}</div>;
}

export function SettingsCardCta({ children }: { children: ReactNode }) {
  return <div className={styles.cta}>{children}</div>;
}

export function SettingsCardCollection({ children }: { children: ReactNode }) {
  return <div className={styles.collection}>{children}</div>;
}
export function SettingsCardCollectionItem({ children }: { children: ReactNode }) {
  return <div className={clsx(styles.collectionItem)}>{children}</div>;
}

export function SettingsCardSection({ children }: { children: ReactNode }) {
  return <div className={styles.section}>{children}</div>;
}

export function SettingsCardFooter({ children }: { children: ReactNode }) {
  return <div className={styles.footer}>{children}</div>;
}
